import compact from 'lodash/compact';
import { useEffect } from 'react';
import styled from 'styled-components';

import { Breadcrumb } from 'ui/breadcrumb';
import { InputSearch } from 'ui/inputs/inputSearch';
import { PAGE_HEADER_VARIANTS } from 'ui/pageHeader/constants';
import { spacing } from 'ui/theme';

import { defaults } from '../../constants';
import { useAssetsQueryContext } from '../../contexts/AssetsQueryContext';
import { usePanelContext } from '../../contexts/Panel';
import { useUploadAssetsContext } from '../../contexts/UploadAssetsContext';
import { useTranslations } from '../../hooks/useTranslations';
import { DropZoneArea, PanelIntent, PanelView } from '../../types';
import { AssetListEmptyState } from '../AssetListEmptyState';
import { DropZone } from '../DropZone';
import { UploadButtonCombo, UploadComboConfig } from '../UploadButtonCombo';
import { AssetList } from './AssetList';

const ListViewBody = styled.div`
  display: flex;
  height: 100%;
  flex: 1 1 auto;
  flex-direction: column;
  row-gap: ${spacing.base};
`;

const ControlSection = styled.div`
  display: flex;
  column-gap: ${spacing.half};

  > div:first-child {
    flex: 1 1 auto;
  }
`;

export const testIds = {
  breadcrumb: 'cl-panel-list-breadcrumb',
  inputSearch: 'cl-panel-list-input-search',
};

export const PanelViewList = () => {
  const { t } = useTranslations();
  const { activeDropZone, setMaxBatchSize } = useUploadAssetsContext();
  const { assetCount, isLoading: isFetchingAssets } = useAssetsQueryContext();
  const { panelSearch, setPanelSearch, panelIntent, setPanelView } = usePanelContext();
  // show the asset list when there is at least one asset && the user is not currently dragging over
  const showAssets = assetCount && !(activeDropZone === DropZoneArea.Panel);

  const uploadComboConfig: UploadComboConfig = {
    label: t('button.upload'),
    onClick: () => setPanelView(PanelView.Upload),
    actions: compact([
      panelIntent !== PanelIntent.Browse && {
        content: t('menu.linkRemote'),
        id: 'btn-combo-link-remote',
        onClick: () => setPanelView(PanelView.RemoteUrl),
      },
    ]),
  };

  useEffect(() => {
    setMaxBatchSize(defaults.maxBatchSize);
  }, []);

  return (
    <ListViewBody>
      {panelIntent === PanelIntent.Edit && (
        <section data-test={testIds.breadcrumb}>
          <Breadcrumb
            isButton
            breadcrumb={{
              content: t('link.imageDetails'),
              onClick: () => setPanelView(PanelView.Detail),
            }}
            variant={PAGE_HEADER_VARIANTS.SECONDARY}
          />
        </section>
      )}
      <ControlSection>
        <InputSearch
          data-test={testIds.inputSearch}
          id={testIds.inputSearch}
          handleClear={() => setPanelSearch('')}
          onChange={setPanelSearch}
          placeholder={t('input.search.placeholder')}
          value={panelSearch}
        />
        <UploadButtonCombo config={uploadComboConfig} />
      </ControlSection>
      <DropZone area={DropZoneArea.Panel} isDisabled={isFetchingAssets}>
        {showAssets ? (
          <AssetList />
        ) : (
          <AssetListEmptyState
            isDragging={activeDropZone === DropZoneArea.Panel}
            isLoading={isFetchingAssets}
            isPanel
          />
        )}
      </DropZone>
    </ListViewBody>
  );
};
